import {
    PATH,
    EDITOR_HOLDER_TAG,
    PROGRESS_TRACKER_HOLDER_CLASS,
    CONTEXT_HELP_HOLDER_CLASS,
    YES_ELF_BODY_HOLDER_ID,
    YES_ELF_AUGMENTATION_HOLDER_ID,
} from './ModelConstant';

import { helpId } from './ModelUtils';

/** Common DOM operations for 'agent' and 'editor' */

const YES_ELF_THEME_ELEMENT_ID = 'yeself-agent-theme';

/**
 * @type {ClientRect}
 */
export const NO_ELEMENT_RECT = {
    top: -5000,
    left: -5000,
    bottom: -5000,
    right: -5000,
    height: 0,
    width: 0,
};

/**
 * @type {Element}
 */
export const NO_ELEMENT = {
    getBoundingClientRect: () => NO_ELEMENT_RECT,
};

export const createEditorHolder = () => document.createElement(EDITOR_HOLDER_TAG);

export const getYesElfBodyHolder = () => document.querySelector(`#${YES_ELF_BODY_HOLDER_ID}`);

export const getYesElfAugmentationHolder = () =>
    document.querySelector(`#${YES_ELF_AUGMENTATION_HOLDER_ID}`);

export const editorHolder = () => document.getElementsByTagName(EDITOR_HOLDER_TAG)[0];

export const progressTrackerHolder = () =>
    document.getElementsByClassName(PROGRESS_TRACKER_HOLDER_CLASS)[0];

export const getContextHelpHolder = () => document.getElementsByClassName(CONTEXT_HELP_HOLDER_CLASS)[0];

export const getYesElfTheme = (guideId) =>
    document.getElementById(`${YES_ELF_THEME_ELEMENT_ID}-${guideId}`);

export const createYesElfTheme = (guideId) => {
    const style = document.createElement('style');
    style.setAttribute('id', `${YES_ELF_THEME_ELEMENT_ID}-${guideId}`);
    style.setAttribute('rel', guideId);
    return style;
};

export const elementCountByXpath = (path = '') => {
    try {
        return document.evaluate && path && path !== PATH.NOT_RECORDED_YET
            ? document.evaluate(`count(${path})`, document, null, XPathResult.NUMBER_TYPE, null)
                  .numberValue
            : 0;
    } catch (err) {
        return 0;
    }
};

/**
 * Returns body margin only when it will affect element position
 */
export const getBodyMarginRect = () => {
    const { body } = document;
    const bodyStyle = window.getComputedStyle(body) || body.currentStyle;

    return {
        top: bodyStyle.position === 'relative' ? parseFloat(bodyStyle.marginTop) : 0,
        left: bodyStyle.position === 'relative' ? parseFloat(bodyStyle.marginLeft) : 0,
        bottom: bodyStyle.position === 'relative' ? parseFloat(bodyStyle.marginBottom) : 0,
        right: bodyStyle.position === 'relative' ? parseFloat(bodyStyle.marginRight) : 0,
    };
};

export const helpElement = (guideId) => document.getElementById(helpId(guideId));

/** Bulgarian constant ... */
const SCROLLBAR_TOLERANCE = 50;
export const elementHasScrollbar = (el) => ({
    horizontal:
        el &&
        el.scrollWidth &&
        el.scrollWidth > el.offsetWidth + SCROLLBAR_TOLERANCE &&
        window.getComputedStyle(el)['overflow-x'] !== 'visible',
    vertical:
        el &&
        el.scrollHeight &&
        el.scrollHeight > el.offsetHeight + SCROLLBAR_TOLERANCE &&
        window.getComputedStyle(el)['overflow-y'] !== 'visible',
});

/**
 * @param {Element} element
 * @param {Array<Element>} list
 *
 * @returns {Array<Element>}
 */
export const collectViewports = (element, list = []) => {
    const { parentElement } = element;
    const scrollbars = elementHasScrollbar(parentElement);
    const newList = scrollbars.horizontal || scrollbars.vertical ? [...list, parentElement] : list;

    if (element && parentElement && parentElement !== document.body) {
        return collectViewports(parentElement, newList);
    }
    return list;
};

const analyzeCss = (cssContent = '') => {
    let updatedCss = '';
    const arrayOfLines = cssContent.split('\n');
    for (let i = 0; i < arrayOfLines.length; i += 1) {
        updatedCss = `${updatedCss}\n${arrayOfLines[i].trim()}`;
    }
    return updatedCss;
};

export const insertCustomCssTheme = (guideId, css) => {
    if (!css) {
        return;
    }

    const elementExists = getYesElfTheme(guideId);

    if (elementExists) {
        if (elementExists.parentNode) {
            // for EI need to use removeChild
            elementExists.parentNode.removeChild(elementExists);
        }
    }

    const style = createYesElfTheme(guideId);
    const customizedCss = analyzeCss(css);
    const textNode = document.createTextNode(customizedCss);

    style.appendChild(textNode);
    document.head.appendChild(style);
};

export const getCookie = (sKey) => {
    if (!sKey) {
        return null;
    }
    return (
        decodeURIComponent(
            document.cookie.replace(
                new RegExp(
                    `(?:(?:^|.*;)\\s*${encodeURIComponent(sKey).replace(
                        /[-.+*]/g,
                        '\\$&'
                    )}\\s*\\=\\s*([^;]*).*$)|^.*$`
                ),
                '$1'
            )
        ) || null
    );
};

export const findParentNode = (callback) => (element) => {
    if (!element || !callback || element === document.body) {
        return null;
    }
    return callback(element) ? element : findParentNode(callback)(element.parentNode);
};

/**
 * @param {string} html
 *
 * @return {string}
 */
export const textFromHtml = (html) => {
    const tempDocument = document.createElement('div');
    tempDocument.innerHTML = html;
    return html ? tempDocument.firstChild.textContent.trim() : '';
};

const isStylePropertySupported = (propertyName, element) => propertyName in element.style;
export const isBackdropFilterSupported = () =>
    isStylePropertySupported('backdrop-filter', document.body);

export const getDocumentHeight = () =>
    Math.max(
        document.documentElement.offsetHeight,
        document.documentElement.scrollHeight,
        window.innerHeight
    );

export const getDocumentWidth = () =>
    Math.max(document.body.scrollWidth, document.documentElement.scrollWidth, window.innerWidth);

/**
 * @param {string} str
 * @return {string | null}
 */
export const stripHtml = (str) => {
    const doc = new DOMParser().parseFromString(str, 'text/html');

    return doc.body.textContent;
};

const getNodeId = (node) => node.id ? `#${node.id}` : '';
const getNodeClass = (node) => !node.classList ? '' : Array.from(node.classList.values()).map(cssClass => `.${cssClass}`).join('');

export const getNodeSelector = (node) => `${node.localName || node.nodeName}${getNodeId(node)}${getNodeClass(node)}`;
export const isNonBody = (targetElement) => targetElement === window || targetElement === document || targetElement.nodeName === 'HTML';
export const isSlotElement = (targetElement) => targetElement.nodeName === 'SLOT';
export const isDocumentFragment = (node) => node.nodeName === '#document-fragment';